import { PoliciesBaseDataModel } from '@/features/project/_models/filter-base-data';
import { t } from '@/i18n';
import type { booleanStringType } from '@/utils';
import { z } from 'zod';

export const PublishedLabelModifier = {
  true: t('published'),
  false: t('not-published'),
} as const satisfies Record<z.infer<typeof booleanStringType>, string>;

export const NestedPoliciesModel = z.object({
  result: PoliciesBaseDataModel,
  projectCount: z.number(),
});

export const ProjectTableColumnSchema = z.enum([
  'name',
  'isPublished',
  'inspectionByExternalExpertReviewType',
  'candidateReasonsOfInspectionByExternalExpert',
  'lastImplementedFiscalYear',
  'ministryName',
  'projectNumber',
  'budgetData',
  'finalizedAmountForInitialBudget',
  'organizationLabel',
  'purpose',
  'task',
  'previousYearExecutedAmounts',
  'previousYearExecutionRate',
  'nextYearRequestedAmount',
  'finalizedAmountForSupplementaryBudget',
  'finalizedAmountForCarriedForward',
  'finalizedAmountForReserve',
  'finalizedAmount',
  'startYear',
  'endYear',
  'fundOwner',
  'fiscalYearOfCreation',
  'endDateOfNewApplicationReceiving',
  'scheduledEndDate',
  'administrativeExpenses',
  'previousYearTotalIncome',
  'previousYearTotalExpense',
  'amountReturnedToTheNationalTreasury',
  'fundSheetNumber',
  'fundSheetBranchNumber',
  'operationForms',
  'businessForms',
  'beginningOfTheFundBalance',
  'ownershipRatio',
  'previousYearAdministrativeExpenseRate',
  'previousYearDivergenceRate',
  'mainPayment',
  'paymentAmount',
]);

export type ProjectTableColumn = z.infer<typeof ProjectTableColumnSchema>;

// 定数として使いやすいようにオブジェクトも用意
export const PROJECT_TABLE_COLUMN_KEYS = {
  NAME: 'name',
  IS_PUBLISHED: 'isPublished',
  INSPECTION_BY_EXTERNAL_EXPERT_REVIEW_TYPE:
    'inspectionByExternalExpertReviewType',
  CANDIDATE_REASONS_OF_INSPECTION_BY_EXTERNAL_EXPERT:
    'candidateReasonsOfInspectionByExternalExpert',
  LAST_IMPLEMENTED_FISCAL_YEAR: 'lastImplementedFiscalYear',
  MINISTRY_NAME: 'ministryName',
  PROJECT_NUMBER: 'projectNumber',
  BUDGET_DATA: 'budgetData',
  FINALIZED_AMOUNT_FOR_INITIAL_BUDGET: 'finalizedAmountForInitialBudget',
  ORGANIZATION_LABEL: 'organizationLabel',
  PURPOSE: 'purpose',
  TASK: 'task',
  PREVIOUS_YEAR_EXECUTED_AMOUNTS: 'previousYearExecutedAmounts',
  PREVIOUS_YEAR_EXECUTION_RATE: 'previousYearExecutionRate',
  NEXT_YEAR_REQUESTED_AMOUNT: 'nextYearRequestedAmount',
  FINALIZED_AMOUNT_FOR_SUPPLEMENTARY_BUDGET:
    'finalizedAmountForSupplementaryBudget',
  FINALIZED_AMOUNT_FOR_CARRIED_FORWARD: 'finalizedAmountForCarriedForward',
  FINALIZED_AMOUNT_FOR_RESERVE: 'finalizedAmountForReserve',
  FINALIZED_AMOUNT: 'finalizedAmount',
  START_YEAR: 'startYear',
  END_YEAR: 'endYear',
  FUND_OWNER: 'fundOwner',
  FISCAL_YEAR_OF_CREATION: 'fiscalYearOfCreation',
  END_DATE_OF_NEW_APPLICATION_RECEIVING: 'endDateOfNewApplicationReceiving',
  SCHEDULED_END_DATE: 'scheduledEndDate',
  ADMINISTRATIVE_EXPENSES: 'administrativeExpenses',
  PREVIOUS_YEAR_TOTAL_INCOME: 'previousYearTotalIncome',
  PREVIOUS_YEAR_TOTAL_EXPENSE: 'previousYearTotalExpense',
  AMOUNT_RETURNED_TO_THE_NATIONAL_TREASURY:
    'amountReturnedToTheNationalTreasury',
  FUND_SHEET_NUMBER: 'fundSheetNumber',
  FUND_SHEET_BRANCH_NUMBER: 'fundSheetBranchNumber',
  OPERATION_FORMS: 'operationForms',
  BUSINESS_FORMS: 'businessForms',
  BEGINNING_OF_THE_FUND_BALANCE: 'beginningOfTheFundBalance',
  OWNERSHIP_RATIO: 'ownershipRatio',
  PREVIOUS_YEAR_ADMINISTRATIVE_EXPENSE_RATE:
    'previousYearAdministrativeExpenseRate',
  PREVIOUS_YEAR_DIVERGENCE_RATE: 'previousYearDivergenceRate',
  MAIN_PAYMENT: 'mainPayment',
  PAYMENT_AMOUNT: 'paymentAmount',
} as const satisfies Record<string, ProjectTableColumn>;

// TODO change label on change translation
export const ProjectTableColumnLabelModifier = {
  name: t('project'),
  ministryName: t('ministry'),
  projectNumber: t('project-number'),
  startYear: t('start-fiscal-year'),
  budgetData: t('budget-graph'),
  finalizedAmountForInitialBudget: t('initial-finalized-amount'),
  organizationLabel: t('organization-in-charge'),
  endYear: t('end-fiscal-year'),
  purpose: t('project-purpose'),
  task: t('project-task'),
  previousYearExecutedAmounts: t('previous-fiscal-year-executed'),
  previousYearExecutionRate: t('previous-year-execution-rate'),
  nextYearRequestedAmount: t('next-request-amount'),
  finalizedAmountForSupplementaryBudget: t('supplementary-finalized-amount'),
  finalizedAmountForCarriedForward: t('carry-over-finalized-amount'),
  finalizedAmountForReserve: t('reserve-finalized-amount'),
  finalizedAmount: t('all-finalized-amount'),
  fundOwner: t('fund-owner'),
  fiscalYearOfCreation: t('fiscal-year-of-creation'),
  endDateOfNewApplicationReceiving: t('end-date-of-new-application'),
  scheduledEndDate: t('fund-scheduled-end-period'),
  administrativeExpenses: t('previous-year-of-administrative-expenses'),
  previousYearTotalIncome: t('previous-year-of-total-income'),
  previousYearTotalExpense: t(
    'previous-year-of-total-expense-thousand-yen-unit',
  ),
  amountReturnedToTheNationalTreasury: t(
    'previous-year-of-amount-of-national-treasury-return',
  ),
  fundSheetNumber: t('fund-sheet-number'),
  fundSheetBranchNumber: t('fund-sheet-branch-number'),
  operationForms: t('operation-form'),
  businessForms: t('business-form'),
  beginningOfTheFundBalance: t(
    'fund-balance-at-the-end-of-previous-fiscal-year',
  ),
  ownershipRatio: t('ownership-ratio'),
  isPublished: t('publish-ability'),
  inspectionByExternalExpertReviewType: t('inspection-target'),
  candidateReasonsOfInspectionByExternalExpert: t(
    'candidate-reasons-of-inspection-by-external-expert',
  ),
  mainPayment: t('main-payment'),
  paymentAmount: t('amount-of-expenditures'),
  previousYearAdministrativeExpenseRate: t(
    'previous-year-of-administrative-expenses-rate',
  ),
  previousYearDivergenceRate: t('previous-year-of-divergence-rate'),
  lastImplementedFiscalYear: t('projects.last-implemented-fiscal-year'),
} satisfies Record<ProjectTableColumn, string>;
